import { useState, useEffect, useCallback } from 'react';
import { EditOutlined, FormOutlined } from '@ant-design/icons';
import { AModal, ASwitch, AInput, ASelect } from '../../../components';
import { Form } from 'antd';
import { useToast } from '../../../hooks/toast';
import {
  schemaHolidayCreate,
  schemaHolidayEdit,
} from '../schemas/yup.holiday.schema';
import { IHoliday } from '../../../shared/interfaces';
import { days, months } from '../Others/holidays.enum';
import { patchHoliday, postHoliday } from '../../../http';
import { useI18n } from '../../../shared/i18n';

interface IHolidayProps {
  open: boolean;
  close: () => void;
  holiday: IHoliday | null;
  setHolidays: React.Dispatch<React.SetStateAction<IHoliday[]>>;
}

const HolidayCreateEdit: React.FC<IHolidayProps> = ({
  open,
  close,
  holiday,
  setHolidays,
}) => {
  const { addToast } = useToast();
  const { t } = useI18n();
  const [description, setDescription] = useState<string>('');
  const [day, setDay] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [status, setStatus] = useState<boolean>(false);

  const handleCreate = useCallback(async () => {
    schemaHolidayCreate
      .validate({
        description,
        day,
        month,
      })
      .then(async (validated) => {
        await postHoliday(validated)
          .then((data) => {
            setHolidays((state) => [...state, data]);
            addToast({
              type: 'success',
              title: t('holidaysModal.toast.successTitle'),
              description: t('holidaysModal.toast.createdDescription'),
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('holidaysModal.toast.errorTitle'),
              description:
                error.response.data.error ??
                t('holidaysModal.toast.createError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('holidaysModal.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [description, day, month, status, t, addToast, close, setHolidays]);

  const handleEdit = useCallback(async () => {
    schemaHolidayEdit
      .validate({
        description,
        day,
        month,
        status,
      })
      .then(async (validated) => {
        await patchHoliday(holiday?.id, validated)
          .then((data) => {
            setHolidays((state) =>
              state.map((e) => (e.id !== data.id ? e : data)),
            );
            addToast({
              type: 'info',
              title: t('holidaysModal.toast.infoTitle'),
              description: t('holidaysModal.toast.updatedDescription'),
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('holidaysModal.toast.errorTitle'),
              description:
                error.response.data.error ?? t('holidaysModal.toast.editError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('holidaysModal.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [
    addToast,
    close,
    day,
    description,
    holiday?.id,
    month,
    setHolidays,
    status,
    t,
  ]);

  const translatedMonths = months.map((monthItem) => ({
    value: monthItem.value,
    label: t(`holidaysModal.months.${monthItem.key}`),
  }));

  useEffect(() => {
    if (holiday) {
      setDescription(holiday.description);
      setDay(holiday.day);
      setMonth(holiday.month);
      setStatus(holiday.status);
    }
  }, [open]);

  return (
    <AModal
      open={open}
      close={close}
      title={
        holiday ? (
          <>
            <EditOutlined /> {t('holidaysModal.titleEdit')}
          </>
        ) : (
          <>
            <FormOutlined /> {t('holidaysModal.titleCreate')}
          </>
        )
      }
      onOk={holiday ? handleEdit : handleCreate}
    >
      <Form layout="vertical" style={{ maxWidth: 600 }}>
        <Form.Item label={t('holidaysModal.fields.description')} required>
          <AInput
            placeholder={t('holidaysModal.fields.descriptionPlaceholder')}
            name="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Form.Item>
        <Form.Item label={t('holidaysModal.fields.day')} required>
          <ASelect
            showSearch
            placeholder={t('holidaysModal.fields.dayPlaceholder')}
            optionFilterProp="children"
            onChange={(e) => setDay(e)}
            filterOption={(input, option: any) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            value={day}
            allowClear
            options={days.map((day) => ({
              label: day.label,
              value: day.value,
            }))}
          />
        </Form.Item>
        <Form.Item label={t('holidaysModal.fields.month')} required>
          <ASelect
            showSearch
            placeholder={t('holidaysModal.fields.monthPlaceholder')}
            optionFilterProp="children"
            onChange={(e) => setMonth(e)}
            filterOption={(input, option: any) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            value={month}
            allowClear
            options={translatedMonths}
          />
        </Form.Item>
        {holiday && (
          <Form.Item label={t('holidaysModal.fields.active')}>
            <ASwitch
              checked={status}
              checkedChildren={t('common.yes')}
              unCheckedChildren={t('common.no')}
              onChange={() => {
                setStatus(!status);
              }}
            />
          </Form.Item>
        )}
      </Form>
    </AModal>
  );
};

export default HolidayCreateEdit;
